import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import axios from 'axios';

// Eager pages (first paint)
import GatewayPage from './pages/GatewayPage';
import MaintenancePage from './pages/MaintenancePage';
import LoginPage from './pages/LoginPage';

// Lazy pages (code splitting: chargées à la demande)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const CertificateVerify = lazy(() => import('./pages/CertificateVerify'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
const SecurityLockPage = lazy(() => import('./pages/SecurityLockPage'));
const RequestAccessPage = lazy(() => import('./pages/RequestAccessPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Institutions = lazy(() => import('./pages/Institutions'));
const InstitutionDetail = lazy(() => import('./pages/InstitutionDetail'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const SubmitProject = lazy(() => import('./pages/SubmitProject'));
const RoadshowPage = lazy(() => import('./pages/RoadshowPage'));
const DealPipeline = lazy(() => import('./pages/DealPipeline'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const ImpactDashboard = lazy(() => import('./pages/ImpactDashboard'));
const KnowledgeHub = lazy(() => import('./pages/KnowledgeHub'));
const TrainingCenter = lazy(() => import('./pages/TrainingCenter'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const TrustPage = lazy(() => import('./pages/TrustPage'));
const WorklistPage = lazy(() => import('./pages/WorklistPage'));
const BriefingPage = lazy(() => import('./pages/BriefingPage'));
import OnboardingTour from './components/OnboardingTour';
import OnboardingWizard from './components/OnboardingWizard';
import NdaAcceptance from './components/NdaAcceptance';
import DealRoomTour from './components/DealRoomTour';
import FeedbackWidget from './components/FeedbackWidget';
import TerminalChrome from './components/terminal/TerminalChrome';
import translations from './i18n';
import { LANGUAGES, READY_LANGS } from './i18n/config';
import { SUPPORTED_CURRENCY_CODES, currencyForInstitution, formatMoney } from './lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
localStorage.removeItem('idfc_access_token');
localStorage.removeItem('idfc_refresh_token');
const API = `${BACKEND_URL}/api`;

// Session lives ONLY in httpOnly cookies (never readable by scripts)
axios.defaults.withCredentials = true;
export const session = { active: false };
axios.interceptors.request.use((config) => {
  const bypass = localStorage.getItem('gcfe_bypass');
  if (bypass) {
    config.headers['X-Maintenance-Bypass'] = bypass;
  }
  return config;
});

// Axios response interceptor: on 401, refresh the cookie session once and retry
let refreshPromise = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthRoute = original?.url?.includes('/auth/');
    if (status === 401 && original && !original._retried && !isAuthRoute && session.active) {
      original._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API}/auth/refresh`, {}).finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return axios(original);
      } catch {
        session.active = false;
      }
    }
    return Promise.reject(error);
  }
);

// Language Context
const LangContext = createContext();

export const useLang = () => {
  const context = useContext(LangContext);
  if (!context) throw new Error('useLang must be used within LangProvider');
  return context;
};

export const useT = () => {
  const { lang } = useLang();
  return (key, params) => {
    let text = translations[lang]?.[key] || translations.en?.[key] || translations.fr?.[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, v);
      });
    }
    return text;
  };
};

const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem('idfc-lang');
    return READY_LANGS.includes(saved) ? saved : 'en';
  });

  const setLanguage = useCallback((code) => {
    if (!READY_LANGS.includes(code)) return;
    localStorage.setItem('idfc-lang', code);
    localStorage.setItem('idfc-lang-manual', '1');
    setLangState(code);
    if (session.active) {
      axios.put(`${API}/users/me/language`, { language: code }).catch(() => {});
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLangState(prev => {
      const next = prev === 'fr' ? 'en' : 'fr';
      localStorage.setItem('idfc-lang', next);
      localStorage.setItem('idfc-lang-manual', '1');
      if (session.active) {
        axios.put(`${API}/users/me/language`, { language: next }).catch(() => {});
      }
      return next;
    });
  }, []);

  // Auto-select by profile: only applies if the user has not manually chosen a language.
  const autoSetLanguage = useCallback((code) => {
    if (!READY_LANGS.includes(code)) return;
    if (localStorage.getItem('idfc-lang-manual')) return;
    localStorage.setItem('idfc-lang', code);
    setLangState(code);
  }, []);

  // Apply the member's own saved preference, and nothing else: the platform is
  // English by default for everyone, like the invitation emails and the terms
  // screen. There is deliberately NO fallback to the institution's language —
  // an analyst of a French-speaking institution used to land on a French UI
  // although their invitation was in English. The FR/EN switch stays one click
  // away and is remembered once used.
  const syncFromServer = useCallback((preferred) => {
    if (preferred && READY_LANGS.includes(preferred)) {
      localStorage.setItem('idfc-lang', preferred);
      localStorage.setItem('idfc-lang-manual', '1');
      setLangState(preferred);
    }
  }, []);

  const value = useMemo(() => ({ lang, toggleLang, setLanguage, autoSetLanguage, syncFromServer, languages: LANGUAGES }), [lang, toggleLang, setLanguage, autoSetLanguage, syncFromServer]);

  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
};

// Currency Context
const CurrencyContext = createContext();

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};

export const useMoney = () => {
  const { currency } = useCurrency();
  const { lang } = useLang();
  return useCallback((valueUSD) => formatMoney(valueUSD, currency, lang), [currency, lang]);
};

const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem('idfc-currency');
    return SUPPORTED_CURRENCY_CODES.includes(saved) ? saved : 'USD';
  });

  const setCurrency = useCallback((code) => {
    if (!SUPPORTED_CURRENCY_CODES.includes(code)) return;
    localStorage.setItem('idfc-currency', code);
    localStorage.setItem('idfc-currency-manual', '1');
    setCurrencyState(code);
    if (session.active) {
      axios.put(`${API}/users/me/currency`, { currency: code }).catch(() => {});
    }
  }, []);

  const syncCurrencyFromServer = useCallback((preferred, institutionId) => {
    if (preferred && SUPPORTED_CURRENCY_CODES.includes(preferred)) {
      localStorage.setItem('idfc-currency', preferred);
      localStorage.setItem('idfc-currency-manual', '1');
      setCurrencyState(preferred);
    } else if (institutionId && !localStorage.getItem('idfc-currency-manual')) {
      const c = currencyForInstitution(institutionId);
      localStorage.setItem('idfc-currency', c);
      setCurrencyState(c);
    }
  }, []);

  const value = useMemo(() => ({ currency, setCurrency, syncCurrencyFromServer }), [currency, setCurrency, syncCurrencyFromServer]);
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const theme = 'dark';

  useEffect(() => {
    localStorage.setItem('idfc-theme', 'dark');
    document.documentElement.classList.remove('light');
    document.documentElement.classList.add('dark');
  }, []);

  const toggleTheme = useCallback(() => {}, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      session.active = true;
      setUser(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {});
          const retryRes = await axios.get(`${API}/auth/me`);
          session.active = true;
          setUser(retryRes.data);
        } catch {
          session.active = false;
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback((authResponse) => {
    session.active = true;
    setUser(authResponse.user || authResponse);
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      // continue
    }
    session.active = false;
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout, checkAuth }), [user, loading, login, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#112C38] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#45B08F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#112C38] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#45B08F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function RealtimeNotifications() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    let ws;
    let keepAlive;
    let retry = 0;
    let closed = false;
    let reconnectTimer;

    const connect = () => {
      if (closed) return;
      const wsUrl = `${API.replace(/^http/, 'ws')}/ws/notifications`;
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { retry = 0; window.__gcfeWs = 'open'; window.dispatchEvent(new CustomEvent('idfc:ws-status', { detail: 'open' })); };
        ws.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data.type === 'notification' && data.notification) {
              const n = data.notification;
              toast(n.title, { description: n.message, duration: 6000 });
              window.dispatchEvent(new CustomEvent('idfc:notification', { detail: n }));
            }
          } catch (_) {}
        };
        ws.onclose = () => {
          window.__gcfeWs = 'closed';
          window.dispatchEvent(new CustomEvent('idfc:ws-status', { detail: 'closed' }));
          if (closed) return;
          retry += 1;
          const delay = Math.min(30000, 1000 * 2 ** Math.min(retry, 5));
          reconnectTimer = setTimeout(connect, delay);
        };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) {}
    };

    connect();
    keepAlive = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    return () => {
      closed = true;
      if (keepAlive) clearInterval(keepAlive);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch (_) {} }
    };
  }, [user?.user_id]);
  return null;
}

function DemoBanner({ show }) {
  const { lang } = useLang();
  const { user } = useAuth();
  if (!show || !user) return null;
  return (
    <div className="fixed inset-x-0 z-[59] bg-[#F5C544] text-[#112C38] text-xs font-semibold tracking-wide text-center py-1.5 px-4 shadow" style={{ bottom: 28 }} data-testid="demo-banner">
      {lang === 'fr'
        ? 'ENVIRONNEMENT DE DÉMONSTRATION — les projets, deals et articles affichés sont fictifs et servent à découvrir la plateforme. Aucune donnée réelle.'
        : 'DEMO ENVIRONMENT — projects, deals and articles shown are fictitious and meant to explore the platform. No real data.'}
    </div>
  );
}

function AppRouter() {
  // MAINTENANCE / CONSTRUCTION MODE — private access via ?acces=<code>. Others are blocked.
  const [gate, setGate] = useState('checking'); // 'checking' | 'blocked' | 'open'
  const [demoMode, setDemoMode] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('acces') || params.get('access');
    if (code) {
      localStorage.setItem('gcfe_bypass', code);
      params.delete('acces'); params.delete('access');
      const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
    }
    // The login/invitation flow must never be blocked by the construction page (clients log in on production).
    const authPaths = ['/login', '/accept-invitation', '/reset-password', '/verify-certificate'];
    const isAuthRoute = authPaths.some((p) => window.location.pathname.startsWith(p));
    axios.get(`${API}/maintenance/status`)
      .then(async (res) => {
        setDemoMode(!!res.data.demo_mode);
        if (!res.data.maintenance || res.data.bypass || isAuthRoute) { setGate('open'); return; }
        // Maintenance is on & no bypass: still let an already-authenticated (invited) user through.
        try { await axios.get(`${API}/auth/me`); setGate('open'); }
        catch { setGate('blocked'); }
      })
      .catch(() => setGate(isAuthRoute ? 'open' : 'blocked'));
  }, []);

  if (gate === 'checking') {
    return (
      <div className="min-h-screen bg-[#112C38] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#45B08F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (gate === 'blocked') {
    return <MaintenancePage />;
  }
  return (
    <>
    <DemoBanner show={demoMode} />
    <NdaAcceptance />
    <OnboardingWizard />
    <OnboardingTour />
    <DealRoomTour />
    <FeedbackWidget />
    <RealtimeNotifications />
    <TerminalChrome />
    <Suspense fallback={
      <div className="min-h-screen bg-[#112C38] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#45B08F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
    <Routes>
      <Route path="/" element={<GatewayPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/verify-certificate" element={<CertificateVerify />} />
      <Route path="/verify-certificate/:certId" element={<CertificateVerify />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/security/lock" element={<SecurityLockPage />} />
      <Route path="/request-access" element={<RequestAccessPage />} />
      <Route path="/roadshow/:token" element={<RoadshowPage />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/briefing" element={
        <ProtectedRoute>
          <BriefingPage />
        </ProtectedRoute>
      } />
      <Route path="/worklist" element={
        <ProtectedRoute>
          <WorklistPage />
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId" element={
        <ProtectedRoute>
          <ProjectDetail />
        </ProtectedRoute>
      } />
      <Route path="/submit-project" element={
        <ProtectedRoute>
          <SubmitProject />
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId/edit" element={
        <ProtectedRoute>
          <SubmitProject />
        </ProtectedRoute>
      } />
      <Route path="/institutions" element={
        <ProtectedRoute>
          <Institutions />
        </ProtectedRoute>
      } />
      <Route path="/institutions/:institutionId" element={
        <ProtectedRoute>
          <InstitutionDetail />
        </ProtectedRoute>
      } />
      <Route path="/pipeline" element={
        <ProtectedRoute>
          <DealPipeline />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/search" element={
        <ProtectedRoute>
          <SearchPage />
        </ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute>
          <ComparePage />
        </ProtectedRoute>
      } />
      <Route path="/impact" element={
        <ProtectedRoute>
          <ImpactDashboard />
        </ProtectedRoute>
      } />
      <Route path="/knowledge" element={
        <ProtectedRoute>
          <KnowledgeHub />
        </ProtectedRoute>
      } />
      <Route path="/formation" element={
        <ProtectedRoute>
          <TrainingCenter />
        </ProtectedRoute>
      } />
      <Route path="/marketplace" element={
        <ProtectedRoute>
          <Marketplace />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPanel />
        </AdminRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <LangProvider>
    <CurrencyProvider>
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
    </CurrencyProvider>
    </LangProvider>
    </ErrorBoundary>
  );
}

export default App;

export { API, BACKEND_URL };
